/* eslint-disable @typescript-eslint/camelcase */
import { yupResolver } from '@hookform/resolvers/yup';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../CommonComponents/OPL/Button/Button';
import { Column } from '../../../../../CommonComponents/OPL/Grid/Column';
import { Grid } from '../../../../../CommonComponents/OPL/Grid/Grid';
import { Row } from '../../../../../CommonComponents/OPL/Grid/Row';
import { Input } from '../../../../../CommonComponents/OPL/Input/Input';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { showError } from '../../../../../CommonComponents/ToastMessenger/ToastMessengerActions';
import { useProcessPaymentMutation } from '../../../../api/useProcessPaymentMutation';
import { SubscriptionTypeDTO } from '../../../../api/useSubscriptionQuery';
import { useCheckout } from '../../../../useCheckout';
import { STRIPE_CARD_ELEMENT_OPTIONS } from './CheckoutForm.constants';
import { CardWrapper } from './CheckoutForm.styles';
import { billingAddressInitialValues, billingAddressValidation } from './CheckoutForm.validation';

interface BillingAddress {
  address1: string;
  address2?: string;
  city: string;
  country: string;
  state: string;
  zip: string;
}

export const CheckoutForm: React.FC = () => {
  const [isCardValid, setIsCardValid] = useState(false);

  const { data, isSuccess } = useCheckout();
  const { mutateAsync: processPayment, isLoading } = useProcessPaymentMutation();

  const dispatch = useDispatch();

  const stripe = useStripe();
  const elements = useElements();

  const formBag = useForm<BillingAddress>({
    defaultValues: billingAddressInitialValues,
    resolver: yupResolver(billingAddressValidation),
  });

  useEffect(() => {
    const subscriptionType = data?.subscriptionType;
    const hasSubscription =
      subscriptionType === SubscriptionTypeDTO.Trial ||
      subscriptionType === SubscriptionTypeDTO.Active;

    if (isSuccess && hasSubscription) {
      ADV.util.Router.go2('FatalError');
    }
  }, [isSuccess, data]);

  const handleSubmit = async (billingAddress: BillingAddress) => {
    if (!stripe || !elements || !isCardValid) {
      return;
    }

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      return;
    }

    const billingData = {
      address_line1: billingAddress.address1,
      address_line2: billingAddress.address2,
      address_city: billingAddress.city,
      address_country: billingAddress.country,
      address_state: billingAddress.state,
      address_zip: billingAddress.zip,
    };

    const { token, error: tokenError } = await stripe.createToken(cardElement, billingData);

    if (tokenError?.message) {
      dispatch(showError(tokenError));
      return;
    }

    if (token) {
      try {
        await processPayment(token.id);
        ADV.util.Router.go2('GetStarted');
      } catch (error) {
        dispatch(showError(error));
      }
    }
  };

  if (!data) {
    return null;
  }

  const { register } = formBag;
  const {
    formState: { errors },
  } = formBag;

  return (
    <Box
      as="form"
      flex={1}
      display="flex"
      flexDirection="column"
      onSubmit={formBag.handleSubmit(handleSubmit)}
    >
      <Text as="h6" fontSize="h6" fontWeight={600}>
        Billing Address
      </Text>
      <Grid>
        <Row my="24">
          <Column width={12}>
            <Input
              {...register('address1')}
              label="Address Line 1"
              type="text"
              placeholder="Street Address"
              formVariant={errors.address1 ? 'error' : undefined}
              helperText={errors.address1?.message}
              autoComplete="address-line1"
              disabled={isLoading}
            />
          </Column>
        </Row>

        <Row my="24">
          <Column width={12}>
            <Input
              {...register('address2')}
              label="Address Line 2 (Optional)"
              type="text"
              placeholder="Apt, Suite, Floor, etc"
              formVariant={errors.address2 ? 'error' : undefined}
              helperText={errors.address2?.message}
              autoComplete="address-line2"
              disabled={isLoading}
            />
          </Column>
        </Row>

        <Row my="24">
          <Column width={12}>
            <Input
              {...register('country')}
              label="Country"
              type="text"
              placeholder="Country"
              formVariant={errors.country ? 'error' : undefined}
              helperText={errors.country?.message}
              autoComplete="country-name"
              disabled={isLoading}
            />
          </Column>
        </Row>

        <Row my="24">
          <Column width={6}>
            <Input
              {...register('city')}
              label="City"
              type="text"
              placeholder="City"
              formVariant={errors.city ? 'error' : undefined}
              helperText={errors.city?.message}
              autoComplete="address-level2"
              disabled={isLoading}
            />
          </Column>
          <Column width={3}>
            <Input
              {...register('state')}
              label="State"
              type="text"
              placeholder="State"
              formVariant={errors.state ? 'error' : undefined}
              helperText={errors.state?.message}
              autoComplete="address-level1"
              disabled={isLoading}
            />
          </Column>

          <Column width={3}>
            <Input
              {...register('zip')}
              label="Zip/Postal Code"
              type="tel"
              placeholder="00000"
              formVariant={errors.zip ? 'error' : undefined}
              helperText={errors.zip?.message}
              autoComplete="postal-code"
              disabled={isLoading}
            />
          </Column>
        </Row>
      </Grid>

      <Text as="h6" fontSize="h6" fontWeight={600} mb="24">
        Card Details
      </Text>
      <CardWrapper>
        <CardElement
          options={{ ...STRIPE_CARD_ELEMENT_OPTIONS, disabled: isLoading }}
          onChange={e => setIsCardValid(e.complete)}
        />
      </CardWrapper>

      <Box mt="48" display="flex" justifyContent="flex-end">
        <Button type="submit" disabled={!isCardValid} isLoading={isLoading}>
          Authorize & Pay
        </Button>
      </Box>
    </Box>
  );
};
